import { useGetAiModelAgentsQuery } from "api/collaborateApi.ts";
import { AtarimEditorRef, Editor, MentionDataUser } from "editor";
import { setMentionUsersGetter, setOnSelectAICallback } from "editor/src/extensions/suggestion.ts";
import { Paperclip, PencilLine, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "sonner";
import { BorderBeam, Button, ShineBorder } from 'ui';
import { cn } from "utils/cn.ts";
import { isEmbedded, isInChromeExtension } from "utils/proxyUtils.ts";
import useShowAI from "../../../../hooks/use-show-ai.ts";
import { presenceService } from '../../../../realtime';
import { ScreenCapture } from "../../../../ScreenCapture.ts";
import { getSelectedSiteCollaborateInfo, getSelectedSiteInfo } from "../../../../store/siteInfoSlice.ts";
import {
  getGeneralTaskModalOpen,
  getIsAIActive,
  getPageId,
  getSelectedTaskId,
  setAnnotateImage,
  setAnnotateModalOpen,
  setIsAIActive
} from '../../../../store/tasksSlice';
import { setIsScreenshotInProgress } from "../../../../store/uiSlice.ts";
import { getUser } from "../../../../store/userSlice.ts";
import CannedResponsesModal from "./canned-response-modal.tsx";

interface CommentBoxProps {
  fileOpen: () => void;
  editorRef: React.RefObject<AtarimEditorRef | null>;
  isLoading?: boolean;
  isSuccess?: boolean;
  handleCreate: (note: boolean, comment: string, files: File[]) => void;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  comment: string;
  setComment: React.Dispatch<React.SetStateAction<string>>;
}

// const lockTitle = "File Attachments"
// const lockDescription = "	Add screenshots, documents, or assets to any task — everything your team needs, right where the feedback happens."
// const lockModalImgSetting = 'https://wpfeedback-image.s3.us-east-2.amazonaws.com/images/integration-lock-images/lock-user-image.jpg'

const aiAvatar: Record<string, string> = {
  lexi: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/lexi.png',
  pixel: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/pixel.png',
  glitch: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/glitch.png',
  navi: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/navi.png',
  index: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/index.png',
  claro: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/claro.png',
  innercircle: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/claro.png',
}

const aiNames: Record<string, string> = {
  lexi: 'Content',
  pixel: 'UI & Design',
  glitch: 'Functionality',
  navi: 'UX & Accessibility',
  index: 'SEO & Performance',
  claro: 'Clarity',
  innercircle: 'All agents',
}

export const CommentBox = ({ fileOpen, isLoading, isSuccess, handleCreate, editorRef, files, setFiles, comment, setComment }: CommentBoxProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const taskId = useSelector(getSelectedTaskId);
  const user = useSelector(getUser)
  const showAI = useShowAI();
  const isAIActive = useSelector(getIsAIActive)
  const [open, setOpen] = useState(false);
  const siteInfo = useSelector(getSelectedSiteInfo);
  const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo)
  const generalTaskModalOpen = useSelector(getGeneralTaskModalOpen);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pageId = useSelector(getPageId);
  const { data: aiAgents } = useGetAiModelAgentsQuery()

  // Generate a unique subscriber ID for this component instance
  const subscriberIdRef = useRef(`comment-box-${Math.random().toString(36).substr(2, 9)}`);
  const subscriberId = subscriberIdRef.current;

  const siteChannelName = pageId ? `pages.${pageId}` : null;

  // Get site presence channel through centralized manager
  const getSitePresenceChannel = useCallback(() => {
    if (!siteChannelName) return null;
    return presenceService.register(siteChannelName, subscriberId);
  }, [siteChannelName, subscriberId]);

  // Emit typing event using whispers on the private task channel
  const emitTyping = useCallback(() => {
    if (taskId && user?.name && window.Echo) {
      const taskChannel = window.Echo.private(`tasks.${taskId}`);
      taskChannel.whisper('comment-typing', { user: user.name });

      const sitePresenceChannel = getSitePresenceChannel();
      if (sitePresenceChannel) {
        sitePresenceChannel.whisper('active-task', {
          userId: user.id,
          timestamp: Date.now(),
          taskId,
        });
        // Note: Presence callbacks (here/joining/leaving) are now handled centrally 
        // in the presence channel manager and update Redux automatically
      }
    }
  }, [taskId, user?.name, user?.id, getSitePresenceChannel]);

  // Register with presence channel manager
  useEffect(() => {
    if (siteChannelName) presenceService.register(siteChannelName, subscriberId);
  }, [siteChannelName, subscriberId]);

  // Cleanup subscriber on unmount
  useEffect(() => {
    return () => {
      if (siteChannelName) presenceService.unregister(siteChannelName, subscriberId);
    };
  }, [siteChannelName, subscriberId]);




  const onSubmit = useCallback(() => {
    const hasComment = comment?.length > 0;
    const hasFiles = files?.length > 0;

    if ((hasComment || hasFiles) && !isLoading) {
      handleCreate(false, comment, files);
      // Only clear content after successful submission
      editorRef.current?.editor?.commands.clearContent();
      setComment('');
      setFiles([]);
    }
  }, [comment, files, isLoading, handleCreate, editorRef, setComment, setFiles]);

  const handleCannedResponse = (response: string) => {
    if (!editorRef.current) return;
    editorRef.current.editor?.commands.insertContent(response);
  };

  // const locked = !user?.raw?.plan?.approve_design_enabled;

  const getScreenshot = async () => {
    // if (locked) {
    //   dispatch(setShowLockFeature({
    //     showLockFeature: true,
    //     lockTitle: "Hotspots",
    //     lockParagraph: "Map out user journeys by adding clickable hotspots to your designs. Drag, drop, and link mockups to create seamless website flows or app UX prototypes.",
    //     lockImg: lockModalImgSetting,
    //     rank: 2,
    //   }));
    //   return;
    // }
    const sc = new ScreenCapture()

    const data = await sc.capture({
      useBrowser: !isEmbedded(),
      useHtml2Canvas: isEmbedded(),
      useChromeApi: isInChromeExtension(),
      onCaptureStart: () => dispatch(setIsScreenshotInProgress(true)),
      onCaptureEnd: () => dispatch(setIsScreenshotInProgress(false)),
    });
    if (!data) {
      console.error("Screenshot data is null or undefined");
      toast.error("Failed to capture screenshot");
      return;
    }
    const blob = dataURItoBlob(data);
    const imageUrl = URL.createObjectURL(blob);
    dispatch(setAnnotateImage(imageUrl));
    dispatch(setAnnotateModalOpen(true));
  }

  function dataURItoBlob(dataURI: string) {
    const mime = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const binary = atob(dataURI.split(',')[1]);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: mime });
  }

  const mentionUsers = useMemo(() => {
    const collaborateUsers = collaborateInfo?.result?.users?.map(user => ({
      value: Number(user.normal_id),
      label: user.label,
      email: user.email,
      avatar: user.avatar || '',
      type: 'user' as const,
      node: 'user-link' as const,
      checked: false,
      responsible: false,
      user_id: user.normal_id,
      role: user.role || '',
      isAI: false,
    })) || [];

    const aiUsers = aiAgents?.map(agent => ({
      value: agent.id,
      label: agent.name,
      email: agent.email || '',
      avatar: aiAvatar[agent.name.toLowerCase()] || '',
      type: 'user' as const,
      node: 'user-link' as const,
      checked: false,
      responsible: false,
      user_id: agent.id,
      role: aiNames[agent.name.toLowerCase()] || agent.name,
      isAI: true,
    })) || [];

    const allAgentsOption = {
      value: 'innercircle',
      label: 'Inner Circle',
      email: '',
      avatar: aiAvatar['innercircle'] || '',
      type: 'user' as const,
      node: 'user-link' as const,
      checked: false,
      responsible: false,
      user_id: 'innercircle',
      role: aiNames['innercircle'], // 'All agents'
      isAI: true,
    };

    // Put "All agents" first in the AI users list
    const allAiUsers = [allAgentsOption, ...aiUsers];

    return [...collaborateUsers, ...allAiUsers];
  }, [collaborateInfo?.result?.users, aiAgents]);


  useEffect(() => {
    // Check if editor reference exists
    if (!editorRef.current) return;

    // Create a variable to track if we've attached the event listener
    let eventAttached = false;

    // Function to attach the event listener
    const attachEventListener = () => {
      if (eventAttached || !editorRef.current?.editor) return;

      const current = editorRef.current;
      const handleUpdate = () => {
        const html = current?.getHTML();
        const isEmpty = !html || html.trim() === "<p><br></p>" || html.trim() === "<p></p>";
        setComment(html || "");
        if (!isEmpty) {
          emitTyping();

          // Clear existing timeout
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }

          // Set new timeout to stop showing typing after 2 seconds of inactivity
          typingTimeoutRef.current = setTimeout(() => {
            // Could emit a "stopped typing" event here if needed
          }, 2000);
        }
      };

      try {
        if (typeof current.editor?.on === 'function') {
          current.editor.on("update", handleUpdate);
          eventAttached = true;

          // Return cleanup function
          return () => {
            if (current.editor && typeof current.editor.off === 'function') {
              current.editor.off("update", handleUpdate);
            }
          };
        }
      } catch (error: unknown) {
        console.error('Error attaching editor event listener:', error);
      }
    };

    // Try to attach immediately
    let cleanup = attachEventListener();

    // If not successful, poll until successful or timeout
    if (!eventAttached) {
      let attempts = 0;
      const maxAttempts = 20; // Try for about 2 seconds total

      const interval = setInterval(() => {
        attempts++;

        // Try to attach the event listener
        if (!eventAttached) {
          cleanup = attachEventListener();
        }

        // Clear interval if we've attached the listener or exceeded max attempts
        if (eventAttached || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 100);

      // Clean up interval on unmount
      return () => {
        clearInterval(interval);
        if (typeof cleanup === 'function') {
          cleanup();
        }
      };
    }

    return cleanup;
  }, [editorRef, emitTyping, setComment]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (isSuccess) {
      editorRef.current.editor?.commands.clearContent();
      setComment('');
    }
  }, [isSuccess, editorRef, setComment]);

  useEffect(() => {
    // No-op: focus is now controlled by parent via ref
    // (left intentionally blank)
  }, [editorRef]);



  useEffect(() => {
    setMentionUsersGetter(mentionUsers as MentionDataUser[]);
    setOnSelectAICallback(() => {
      dispatch(setIsAIActive(true));
    });

    // Cleanup function to remove the callback when component unmounts
    return () => {
      setOnSelectAICallback(null);
      setMentionUsersGetter([]);
      setFiles([]);
      setComment('');
    };
  }, [mentionUsers, dispatch, setFiles, setComment]);



  const hasPermission = useMemo(() => {
    return collaborateInfo?.result?.role === 'admin' || collaborateInfo?.result?.role === 'contributor';
  }, [collaborateInfo?.result?.role]);

  const toggleAIActive = () => {
    dispatch(setIsAIActive(!isAIActive))
    editorRef.current?.editor?.commands.focus();
  }

  // Removing the lock till the API add it to plan
  // const isPaid = useMemo(() => {
  //   return (collaborateInfo?.result.plan?.rank || 0) >= 2
  // }, [collaborateInfo?.result.plan?.rank]);

  return (
    <div className="min-h-24 max-h-40 flex flex-col rounded-lg p-0 relative overflow-hidden">
      <Editor
        data-no-drag
        className="flex-1 mt-0"
        editorClassName={cn('cursor-text p-3 min-h-24 max-h-36 w-full ring-0 outline-none rounded-xl bg-slate-50 border-input dark:bg-background text-sm overflow-auto transition-all duration-100', isAIActive && 'shadow-md ')}
        ref={editorRef}
        showToolbar={false}
        showBubbleMenu={true}
        showLinkPreview={false}
        onSubmit={onSubmit}
        placeholder={isAIActive ? t('comment_box.placeholder.enter_comment_ai', 'Ask the AtariMates...') : t('comment_box.placeholder.enter_comment', 'What do you think?')}
        aria-label={t('comment_box.aria.editor', 'Comment editor')}
      />
      {isAIActive && showAI && (
        <>
          <ShineBorder borderWidth={2} duration={12} shineColor={["#A07CFE", "#FE8FB5", "#FFBE7B"]} />
          <BorderBeam
            duration={4}
            size={125}
            borderWidth={2}
            className="from-transparent via-tertiary to-transparent"
          />
          <BorderBeam
            duration={4}
            delay={2}
            size={125}
            borderWidth={2}
            className="from-transparent via-ata-orange to-transparent"
          />
        </>
      )}
      <div className="absolute bottom-2 right-2 flex justify-end gap-1 bg-transparent dark:bg-background">

        {showAI && !generalTaskModalOpen && (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 hover:bg-transparent"
            tooltip={t('comment_box.tooltip.ai', 'Ask AI to clarify, suggest ideas, or sanity-check your thinking - based on this thread.')}
            toolTipPortal
            aria-label={t('comment_box.aria.ai', 'Open AI client')}
            onClick={toggleAIActive}
          >
            <Sparkles
              size={18}
              aria-hidden="true"
              className={cn('text-gray-400 hover:text-gray-600 dark:hover:text-textcolor', isAIActive && 'text-tertiary')}
            />
          </Button>
        )}

        {showAI && generalTaskModalOpen && (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 hover:bg-transparent"
            tooltip={t('comment_box.tooltip.ai', 'Ask AI to clarify, suggest ideas, or sanity-check your thinking - based on this thread.')}
            toolTipPortal
            aria-label={t('comment_box.aria.ai', 'Open AI client')}
            onClick={toggleAIActive}
          >
            <Sparkles
              size={18}
              aria-hidden="true"
              className={cn('text-gray-400 hover:text-gray-600 dark:hover:text-textcolor', isAIActive && 'text-tertiary')}
            />
          </Button>
        )}
        {(siteInfo?.id && siteInfo.workspace_id) && hasPermission &&
          <CannedResponsesModal
            handleSubmit={handleCannedResponse}
            open={open}
            setOpen={setOpen}
            taskId={taskId ?? ''}
            siteId={siteInfo.id}
            workspaceId={siteInfo.workspace_id}
          />}

        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0 hover:bg-transparent"
          // onClick={annotateOpen}
          onClick={() => getScreenshot()}
          tooltip={t('comment_box.tooltip.annotate', 'Annotate a screenshot and attach as a comment')}
          toolTipPortal
          aria-label={t('comment_box.aria.annotate', 'Open screenshot annotation')}
        >
          <PencilLine size={18} aria-hidden="true" className='text-gray-400 hover:text-gray-600 dark:hover:text-textcolor' />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // if (locked) {
            //   dispatch(setShowLockFeature({
            //     showLockFeature: true,
            //     lockTitle: lockTitle,
            //     lockParagraph: lockDescription,
            //     lockImg: lockModalImgSetting,
            //     rank: 2,
            //   }));
            //   return;
            // }
            fileOpen()
          }
          }
          className="h-5 w-5 p-0 hover:bg-transparent group"
          tooltip={t('comment_box.tooltip.attach', 'Attach an item')}
          toolTipPortal
          aria-label={t('comment_box.aria.attach', 'Attach file')}
        >
          <Paperclip
            size={18}
            aria-hidden="true"
            className='text-gray-400 group-hover:text-gray-600 dark:hover:text-textcolor'
          />
        </Button>
      </div>
    </div>
  );
};